// One widget of a dashboard, drawn on its own (VisionPlatform specs/mobile-dashboards-2 MD2-03): the phone apps'
// web-view widget shows it through the embed page, and the web draws a web_widget saved on a dashboard with it too.
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../api/problem";
import { ErrorMessage } from "../../ui/ErrorMessage";
import { Loading } from "../../ui/Spinner";
import type { Widget } from "../../widgets/types";
import { ActionDialog } from "../devices/actions/ActionDialog";
import { useDeviceActions } from "../devices/actions/useDeviceActions";
import { WidgetGrid } from "../devices/page/WidgetGrid";
import { asDocument } from "../dashboards/copy";
import { dashboardWidgets, useNow } from "../dashboards/widgets";
import { useEditedDashboardInput } from "../dashboards/input";
import { neededPlan, widgetDevice } from "../dashboards/planWidgets";
import { dashboardQuery } from "../dashboards/queries";

/** The widget `widgetId` of the dashboard, full size; a web_widget is never drawn inside another one. */
export function pickWidget(widgets: Widget[], widgetId: string): Widget | undefined {
  const w = widgets.find((x) => x.id === widgetId);
  if (!w || w.type === "web_widget") return undefined;
  return { ...w, position: { x: 0, y: 0, w: 12, h: w.position.h } };
}

export function DashboardWidget({
  orgId,
  dashboardId,
  widgetId,
  title,
}: {
  orgId: string;
  dashboardId: string;
  widgetId: string;
  /** The web_widget's own title, shown above the widget when it has one. */
  title?: string;
}) {
  const { t, i18n } = useTranslation();
  const now = useNow();
  const dashboard = useQuery({ ...dashboardQuery(orgId, dashboardId), enabled: !!orgId });
  const widgets = useMemo(() => {
    const doc = asDocument(dashboard.data?.document);
    const w = doc ? pickWidget(dashboardWidgets(doc), widgetId) : undefined;
    return w ? [w] : [];
  }, [dashboard.data, widgetId]);
  const data = useEditedDashboardInput(orgId, widgets, i18n.language);
  const actions = useDeviceActions(
    orgId,
    data.docFor,
    data.infoFor,
    widgets.some((w) => w.type === "scene_button"),
  );

  if (dashboard.isPending) return <Loading />;
  if (dashboard.isError) {
    if (dashboard.error instanceof ApiError && dashboard.error.status === 404)
      return <Gone text={t("embed.notShared")} />;
    return <ErrorMessage error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  }
  if (widgets.length === 0) return <Gone text={t("embed.removed")} />;
  return (
    <div className="flex h-full flex-col">
      {title && <h3 className="text-sm font-medium text-gray-900">{title}</h3>}
      {data.error && <ErrorMessage error={data.error} onRetry={data.refetch} />}
      <WidgetGrid
        widgets={widgets}
        input={data.input}
        inputFor={data.inputFor}
        actions={actions}
        needsPlan={(w) => neededPlan(w, data.pageFor(widgetDevice(w)))}
        now={now}
        orgId={orgId}
        label={widgets[0]?.title ?? t("embed.label")}
      />
      <ActionDialog actions={actions} nameOf={(id) => data.input.devices?.[id]?.name ?? id} />
    </div>
  );
}

function Gone({ text }: { text: string }) {
  return (
    <div className="h-full rounded-md border border-dashed border-gray-300 bg-white p-3 text-sm text-gray-700">
      {text}
    </div>
  );
}
